// AwardFeed.js
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import useApi2Resource from '../../hooks/useApi2Resource';
import usePagedList from '../../hooks/usePagedList';
import FeedItem from '../feed/FeedItem';
import { FeedCard, FeedSection, FeedSegments, LoadMore, PanelState } from '../feed/FeedCard';
import {
  AWARD_GROUPS,
  awardGroupLabel,
  fetchAward,
  AWARD_PAGE_SIZE,
  fetchAwardFeed,
  formatCount,
  formatDay,
  formatDollars,
  titleCaseName,
} from '../../utils/spending';

/**
 * AwardFeed
 *
 * The newest awards (first obligated in the last 7 days) and the largest
 * signed in the last 30, each one a FeedItem so the list reads exactly like
 * the Spending feeds: same card, Source button, save and share.
 *
 *   title    the recipient; tapping it opens the award on USAspending
 *   details  the amount and what it is; the description, in full; the
 *            awarding agency and the day the award was first obligated
 *
 * The date sits in the agency line rather than FeedItem's date row, so it
 * reads "first obligated Aug 31" instead of a bare 8-31-2026 above it.
 *
 * Details opens in place under those lines: whether the award was competed,
 * how many offers came in, and which federal accounts paid for it. An award
 * the agency has not yet linked to an account says so, as a reporting gap.
 *
 * ─── Two ways to show it ──────────────────────────────────────────────────────
 *
 *   preview   on the spending dashboard. The first PREVIEW_COUNT awards of
 *             the chosen feed and kind, then a See all button that opens the
 *             full list on its own page. A full feed of award cards sat
 *             between the reader and the companies and trades panels below
 *             it; two are enough to show what the feed is.
 *   full      at AWARDS_PAGE_PATH (AwardsPage). The paged list with Load
 *             more and the API's notes. It opens on the feed and kind the
 *             preview had selected, carried in the URL (?feed=&group=), so
 *             See all continues what the reader was looking at, and a copied
 *             link opens on the same tabs.
 *
 * What the lines are careful about:
 *
 *   - "Obligated", never "spent" or "paid". Award Amount is the total
 *     obligated on the award to date, so for a multi-year award it covers
 *     more than the window.
 *   - Loans have no Award Amount. Their line shows the Loan Value as the face
 *     value lent, which is never compared with a contract's obligations.
 *   - Defense contract actions arrive about 90 days late, so a 7- or 30-day
 *     window holds almost none of them. The API's own notes say so and are
 *     shown under the full feed, rather than letting the largest buyer look as
 *     though it has stopped buying.
 */

const FEEDS = ['newest', 'largest'];

// Awards shown on the dashboard before See all.
const PREVIEW_COUNT = 2;

// The full feed's own page. Under the Spending tab's path, so the tab stays
// lit there through the bottom bar's prefix check.
export const AWARDS_PAGE_PATH = '/us/spending-dashboard/awards';

export const usaspendingUrl = (awardId) =>
  awardId ? `https://www.usaspending.gov/award/${encodeURIComponent(awardId)}` : null;

// Funding rows name their account and amount under keys that vary by award
// type; the first present one is read.
const firstOf = (row, keys) =>
  keys.map((key) => row?.[key]).find((value) => value !== undefined && value !== null);

const AwardDetail = ({ awardId }) => {
  const { t } = useTranslation('common');
  const award = useApi2Resource(() => fetchAward(awardId), [awardId]);

  if (award.loading || award.error) {
    return <PanelState loading={award.loading} error={award.error} />;
  }

  const data = award.data || {};
  const contract = data.latest_transaction_contract_data || {};
  const funding = Array.isArray(data.funding) ? data.funding : [];
  const offers = contract.number_of_offers_received;

  return (
    <div className="award-detail">
      {(contract.extent_competed_description || (offers !== undefined && offers !== null)) && (
        <dl className="award-facts">
          {contract.extent_competed_description && (
            <>
              <dt>{t('common:competition', 'Competition')}</dt>
              <dd>{titleCaseName(contract.extent_competed_description)}</dd>
            </>
          )}
          {offers !== undefined && offers !== null && (
            <>
              <dt>{t('common:offersReceived', 'Offers received')}</dt>
              <dd>{formatCount(Number(offers))}</dd>
            </>
          )}
        </dl>
      )}

      <div className="award-funding">
        <div className="spending-list-title">{t('common:paidFrom', 'Paid from')}</div>
        {funding.length ? (
          <ul className="award-funding-list">
            {funding.map((fundingRow, index) => (
              <li key={index}>
                <span>
                  {titleCaseName(
                    String(
                      firstOf(fundingRow, ['account_title', 'federal_account_name', 'federal_account', 'name']) ||
                        t('common:unnamedAccount', 'Unnamed account')
                    )
                  )}
                </span>
                <span className="award-funding-amount">
                  {formatDollars(
                    Number(firstOf(fundingRow, ['transaction_obligated_amount', 'obligated_amount', 'amount']))
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="spending-hint">
            {t(
              'common:noFundingLinked',
              'The agency has not linked this award to a federal account yet. That is a reporting gap, not an absence of funding.'
            )}
          </p>
        )}
      </div>
    </div>
  );
};

// One award row from /spending/awards as a FeedItem. Shared with the company
// page's newest-awards list.
export function AwardItem({ row, isLoan = false }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const awardId = row.generated_internal_id;
  const url = usaspendingUrl(awardId);
  const amount = isLoan ? row['Loan Value'] : row['Award Amount'];

  const item = {
    id: awardId || row['Award ID'],
    title: titleCaseName(row['Recipient Name']) || t('common:unnamedRecipient', 'Unnamed recipient'),
    created_at: row['Base Obligation Date'],
    url,
  };

  const signed = formatDay(row['Base Obligation Date']);
  const agencyLine = [
    row['Awarding Agency'],
    signed && t('common:firstObligated', 'first obligated {{date}}', { date: signed }),
  ]
    .filter(Boolean)
    .join(', ');

  const details = [
    <span className="award-amount">
      <span className="award-amount-value">{formatDollars(amount)}</span>{' '}
      <span className="award-amount-label">
        {isLoan ? t('common:faceValue', 'face value') : t('common:obligated', 'obligated')}
      </span>
    </span>,
    row.Description ? <span className="award-description">{row.Description}</span> : null,
    agencyLine ? <span className="award-meta">{agencyLine}</span> : null,
    awardId ? (
      <button
        type="button"
        className="award-details-toggle"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? <ChevronUp size={12} className="me-1" /> : <ChevronDown size={12} className="me-1" />}
        {t('common:details', 'Details')}
      </button>
    ) : null,
    open && awardId ? <AwardDetail awardId={awardId} /> : null,
  ];

  return (
    <FeedItem
      item={item}
      feedName={t('common:federalAwards', 'Federal awards')}
      branch="Spending"
      topic={t('common:federalAwards', 'Federal awards')}
      buttonName={null}
      schema=""
      table=""
      variant="default"
      details={details}
      shareUrl={url}
      showDate={false}
    />
  );
}

const awardKey = (row) => row.generated_internal_id || row.internal_id || row['Award ID'];

// A feed or group read off the URL is used only if it is one the tabs offer,
// so a mistyped link opens on the defaults rather than an empty tab.
const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

function AwardFeed({ preview = false }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The full page opens on whatever the preview had selected. The preview
  // itself always opens on the defaults: the dashboard's URL carries no tabs.
  const [feed, setFeed] = useState(() =>
    preview ? 'newest' : pick(searchParams.get('feed'), FEEDS, 'newest')
  );
  const [group, setGroup] = useState(() =>
    preview ? 'contracts' : pick(searchParams.get('group'), AWARD_GROUPS, 'contracts')
  );

  // Pages by `page` (from 1) and `has_next`. Rows already on the list are
  // skipped: new awards can land between two pages of the newest feed and push
  // ones the reader has seen onto the next.
  //
  // The preview asks for only the rows it shows, so the dashboard does not
  // download a page of awards to draw two of them.
  const pageSize = preview ? PREVIEW_COUNT : AWARD_PAGE_SIZE;
  const awards = usePagedList(
    (pageIndex) =>
      fetchAwardFeed(feed, group, pageSize, pageIndex + 1).then((body) => ({
        rows: Array.isArray(body?.results) ? body.results : [],
        hasMore: Boolean(body?.has_next),
        meta: body,
      })),
    [feed, group, pageSize],
    { keyOf: awardKey }
  );
  const rows = preview ? awards.rows.slice(0, PREVIEW_COUNT) : awards.rows;
  const notes = Array.isArray(awards.meta?.notes) ? awards.meta.notes : [];
  const signedWindow = awards.meta?.signed_window;

  const feedLabel = (id) =>
    id === 'newest'
      ? t('common:newestAwards', 'Newest')
      : t('common:largestAwards', 'Largest this month');

  const openFullFeed = () => {
    const params = new URLSearchParams({ feed, group });
    navigate(`${AWARDS_PAGE_PATH}?${params.toString()}`);
  };

  return (
    <FeedSection
      id="feed"
      title={t('common:awardFeedTitle', 'New federal awards')}
      subtitle={
        signedWindow?.start_date && signedWindow?.end_date
          ? t('common:signedBetween', 'Signed {{start}} to {{end}}', {
              start: formatDay(signedWindow.start_date),
              end: formatDay(signedWindow.end_date),
            })
          : null
      }
    >
      <div className="spending-feed-controls">
        <FeedSegments
          label={t('common:awardFeed', 'Feed')}
          value={feed}
          onChange={setFeed}
          options={FEEDS.map((id) => ({ id, label: feedLabel(id) }))}
        />
        <FeedSegments
          label={t('common:awardGroup', 'Kind of award')}
          value={group}
          onChange={setGroup}
          options={AWARD_GROUPS.map((id) => ({ id, label: awardGroupLabel(id, t) }))}
        />
      </div>

      {(awards.loading || awards.error || !rows.length) && (
        <FeedCard>
          <PanelState
            loading={awards.loading}
            error={awards.error}
            empty={!awards.loading && !awards.error && !rows.length}
          />
        </FeedCard>
      )}

      {!awards.loading &&
        rows.map((row, index) => (
          <AwardItem
            key={row.generated_internal_id || `${row['Award ID']}-${index}`}
            row={row}
            isLoan={group === 'loans'}
          />
        ))}

      {/* Preview: one button to the full page, whenever there is anything to
          see. Full page: the ordinary paged Load more. */}
      {!awards.loading && preview && rows.length > 0 && (
        <LoadMore
          hasMore
          loading={false}
          onClick={openFullFeed}
          label={t('common:seeAllAwards', 'See all new awards')}
        />
      )}

      {!awards.loading && !preview && (
        <LoadMore
          hasMore={awards.hasMore}
          loading={awards.loadingMore}
          error={awards.moreError}
          onClick={awards.loadMore}
          label={t('common:loadMoreAwards', 'Load more awards')}
        />
      )}

      {!preview && notes.length > 0 && (
        <div className="spending-notes">
          {notes.map((note, index) => (
            <p key={index}>{note}</p>
          ))}
        </div>
      )}
    </FeedSection>
  );
}

export default AwardFeed;